import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { toggleDebugPanel, setDebugLogs, clearDebugLogs } from '@/store/slices/debugSlice'
import './DebugPanel.css'

interface DebugLog {
  id: string
  timestamp: Date
  level: 'log' | 'info' | 'warn' | 'error'
  message: string
  data?: any
  source?: string
}

const DebugPanel: React.FC = () => {
  const dispatch = useDispatch()
  const { isEnabled, logs } = useSelector((state: any) => state.debug)
  const formData = useSelector((state: any) => state.formData)
  const appData = useSelector((state: any) => state.appData)
  const user = useSelector((state: any) => state.user)
  
  const [activeTab, setActiveTab] = useState<'logs' | 'state' | 'network'>('logs')
  const [filter, setFilter] = useState<string>('')

  if (!isEnabled) return null

  const filteredLogs = logs.filter((log: DebugLog) => {
    if (!filter) return true
    return (
      log.message.toLowerCase().includes(filter.toLowerCase()) ||
      log.source?.toLowerCase().includes(filter.toLowerCase()) ||
      JSON.stringify(log.data).toLowerCase().includes(filter.toLowerCase())
    )
  })

  const exportLogs = () => {
    const data = {
      logs: filteredLogs,
      state: {
        formData,
        appData,
        user
      },
      timestamp: new Date().toISOString()
    }
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `debug-export-${Date.now()}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const getLogClass = (level: string) => {
    switch (level) {
      case 'error': return 'debug-panel-log-error'
      case 'warn': return 'debug-panel-log-warn'
      case 'info': return 'debug-panel-log-info'
      default: return 'debug-panel-log-default'
    }
  }

  return (
    <div className="debug-panel">
      {/* Header */}
      <div className="debug-panel-header">
        <h2 className="text-lg font-semibold">Debug Panel</h2>
        <button
          onClick={() => dispatch(toggleDebugPanel())}
          className="debug-panel-close-button"
        >
          ✕
        </button>
      </div>

      {/* Tabs */}
      <div className="debug-panel-tabs">
        <button
          className={`debug-panel-tab ${
            activeTab === 'logs' 
              ? 'debug-panel-tab-active' 
              : 'debug-panel-tab-inactive'
          }`}
          onClick={() => setActiveTab('logs')}
        >
          Logs ({logs.length})
        </button>
        <button
          className={`debug-panel-tab ${
            activeTab === 'state' 
              ? 'debug-panel-tab-active' 
              : 'debug-panel-tab-inactive'
          }`}
          onClick={() => setActiveTab('state')}
        >
          State
        </button>
        <button
          className={`debug-panel-tab ${
            activeTab === 'network' 
              ? 'debug-panel-tab-active' 
              : 'debug-panel-tab-inactive'
          }`}
          onClick={() => setActiveTab('network')}
        >
          Network
        </button>
      </div>

      {/* Content */}
      <div className="debug-panel-content">
        {activeTab === 'logs' && (
          <div>
            {/* Controls */}
            <div className="debug-panel-controls">
              <input
                type="text"
                placeholder="Filter logs..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="debug-panel-filter"
              />
              <button
                onClick={() => dispatch(clearDebugLogs())}
                className="debug-panel-button-clear"
              >
                Clear
              </button>
              <button
                onClick={exportLogs}
                className="debug-panel-button-export"
              >
                Export
              </button>
            </div>
            
            {/* Log entries */}
            {filteredLogs.length === 0 ? (
              <div className="debug-panel-empty">
                <p>No logs yet</p>
                <p style={{ fontSize: '0.75rem', opacity: 0.6 }}>
                  Logs will appear here as you interact with the application
                </p>
              </div>
            ) : (
              <div className="debug-panel-logs">
                {filteredLogs.map((log: DebugLog) => (
                <div
                  key={log.id}
                  className={`debug-panel-log-entry ${getLogClass(log.level)}`}
                >
                  <div className="debug-panel-log-meta">
                    <span className="debug-panel-log-time">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </span>
                    {log.source && (
                      <span className="debug-panel-log-source">{log.source}</span>
                    )}
                  </div>
                  <div className="debug-panel-log-message">{log.message}</div>
                  {log.data && (
                    <pre className="debug-panel-log-data">
                      {JSON.stringify(log.data, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'state' && (
          <div className="debug-panel-state-section">
            {/* Form Data */}
            <div>
              <h3 className="debug-panel-state-title">Form Data</h3>
              <pre className="debug-panel-state-content">
                {JSON.stringify(formData, null, 2)}
              </pre>
            </div>
            
            {/* App Data */}
            <div>
              <h3 className="debug-panel-state-title">App Data</h3>
              <pre className="debug-panel-state-content">
                {JSON.stringify(appData, null, 2)}
              </pre>
            </div>
            
            {/* User */}
            <div>
              <h3 className="debug-panel-state-title">User</h3>
              <pre className="debug-panel-state-content">
                {JSON.stringify(user, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {activeTab === 'network' && (
          <div className="debug-panel-network-placeholder">
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔌</div>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#ffffff' }}>
              Network Monitor
            </h3>
            <p>Coming soon - Track API calls, response times, and payloads</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default DebugPanel